<template>
  <div
    class="kf-kpi neumorphism-flat"
    :class="{ 'kf-kpi--clickable': clickable, 'kf-kpi--active': clickable && active }"
    :aria-pressed="clickable ? String(active) : null"
    @click="onClick"
  >
    <template v-if="loading">
      <v-skeleton-loader type="list-item-two-line" class="kf-kpi__skeleton" />
    </template>
    <template v-else>
      <div class="kf-kpi__top">
        <span class="kf-kpi__label">{{ label }}</span>
        <span class="kf-kpi__icon" :style="{ backgroundColor: color + '16', color: color }">
          <!-- Anything without the `mdi-` prefix is treated as a Lucide name,
               so old call sites keep working while new ones move to Lucide. -->
          <v-icon v-if="isMdi" :color="color" size="17">{{ icon }}</v-icon>
          <LucideIcon v-else :name="icon" :size="17" :color="color" />
        </span>
      </div>
      <div class="kf-kpi__value" :title="rawTitle">{{ display }}</div>
      <div v-if="sub" class="kf-kpi__sub">{{ sub }}</div>
    </template>
  </div>
</template>

<script>
import fmt from "@/app-js/karrofin/format";

export default {
  name: "KfKpiCard",
  props: {
    label: { type: String, required: true },
    value: { type: [Number, String], default: 0 },
    icon: { type: String, default: "chart-column" },
    color: { type: String, default: "#1976D2" },
    sub: { type: String, default: "" },
    isCurrency: { type: Boolean, default: false },
    /**
     * Render `value` as a percentage: pass the FRACTION (0.421), not 42.1.
     *
     * A preformatted "42.1%" string cannot go through the normal path --
     * display() falls to fmt.number(), and fmt.number("42.1%") is "-". Hence a
     * third mode rather than letting callers format it themselves.
     */
    isPercent: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    clickable: { type: Boolean, default: false },
    /**
     * Selected state, for the screens where these tiles ARE the filter (the
     * disbursement buckets). Off by default, so the dashboard and notification
     * tiles -- which only report -- look exactly as they did.
     */
    active: { type: Boolean, default: false },
  },
  computed: {
    isMdi() {
      return String(this.icon).startsWith("mdi-");
    },
    display() {
      // Guarded the way fmt.number guards: a null fraction means "there was no
      // denominator", which has to read as "-". fmt.percent alone would turn it
      // into "0.0%", a different and untrue statement.
      if (this.isPercent) {
        return this.value === null || this.value === undefined || this.value === ""
          ? "-"
          : fmt.percent(this.value);
      }
      if (this.isCurrency) return fmt.currencyShort(this.value);
      return fmt.number(this.value);
    },
    // Full-precision value on hover, since the tile itself is abbreviated.
    rawTitle() {
      if (this.isPercent) {
        return this.display === "-" ? "-" : fmt.percent(this.value, 2);
      }
      return this.isCurrency ? fmt.currency(this.value) : fmt.number(this.value);
    },
  },
  methods: {
    onClick() {
      if (this.clickable) this.$emit("click");
    },
  },
};
</script>

<style scoped>
.kf-kpi {
  background: var(--kf-surface);
  border-radius: 10px;
  padding: 14px 16px;
  height: 100%;
  min-height: 104px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.kf-kpi--clickable {
  cursor: pointer;
}
.kf-kpi--clickable:hover {
  transform: translateY(-2px);
}
/* Selected tile. Same brand fill and white text as StatStrip's active segment,
   so a bucket looks picked the same way on every screen. The :not(--active)
   guard on the hover rule above is not needed here -- that rule only moves the
   tile -- but the hover BACKGROUND below has to be darker, or hovering the
   selected tile would repaint it pale and hide its white text. */
.kf-kpi--active {
  background: var(--kf-brand) !important;
  border-color: var(--kf-brand) !important;
}
.kf-kpi--active .kf-kpi__label,
.kf-kpi--active .kf-kpi__value,
.kf-kpi--active .kf-kpi__sub {
  color: #ffffff;
}
.kf-kpi--active.kf-kpi--clickable:hover {
  background: var(--kf-brand-strong) !important;
}
.kf-kpi__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.kf-kpi__label {
  font-size: var(--kf-fs-meta);
  font-weight: 600;
  color: #000;
  letter-spacing: 0.2px;
  line-height: 1.35;
}
.kf-kpi__icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kf-kpi__value {
  font-size: 26px;
  font-weight: 700;
  color: var(--kf-brand);
  line-height: 1.2;
  margin-top: 8px;
  word-break: break-all;
}
.kf-kpi__sub {
  font-size: var(--kf-fs-label);
  color: #000;
  margin-top: 3px;
}
.kf-kpi__skeleton {
  background: transparent;
}
@media (max-width: 600px) {
  .kf-kpi__value {
    font-size: 22px;
  }
}
</style>
